import React, { useState } from 'react';
import { ConfigProvider, Popover } from 'antd';
import { useTheme } from '../../theme/ThemeContext';
import Button from '../../atoms/Button/Button';
import Download from '../../atoms/Icon/Download';
import CheckDone from '../../atoms/Icon/CheckDone';

export type ExportFormat = 'pdf' | 'ppt' | 'docx' | 'xlsx';

export interface ExportOptions {
    includeResponses: boolean;
}

export interface ExportMenuProps {
    format: ExportFormat;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    onExport?: (format: ExportFormat, options: ExportOptions) => void;
    children?: React.ReactNode;
    'data-test'?: string;
    'data-test-id'?: string;
}

const formatLabels: Record<ExportFormat, string> = {
    pdf: 'PDF',
    ppt: 'PPT',
    docx: 'Word',
    xlsx: 'Excel',
};

const ExportMenu: React.FC<ExportMenuProps> = (props) => {
    const { format, open, onOpenChange, onExport, children } = props;

    const theme = useTheme();
    const [includeResponses] = useState(true);

    const handleExport = () => {
        if (onExport) {
            onExport(format, {
                includeResponses,
            });
        }
        // Close popover after export
        if (onOpenChange) {
            onOpenChange(false);
        }
    };

    const content = (
        <div
            className="flex flex-col gap-[8px] p-[12px] rounded-[8px] bg-white"
            data-test={props['data-test']}
            data-test-id={props['data-test-id']}
            data-asper-export-menu
        >
            {/* Header */}
            <div className="flex gap-[8px] items-start">
                <div className="flex items-start pt-[3px] pb-0 px-0 h-[22px]">
                    <Download size={16} color="rgba(0,0,0,0.88)" />
                </div>
                <div className="flex-1 flex flex-col gap-[4px] items-start min-w-0">
                    <p className="font-['Open_Sans'] font-semibold text-[14px] leading-[22px] text-[rgba(0,0,0,0.88)] m-0 w-full whitespace-pre-wrap">
                        Download {formatLabels[format]}
                    </p>

                    {/* Options */}
                    <div className="flex flex-col gap-[10px] items-start w-full">
                        <div className="flex items-center py-[3px]" data-test-id={`${props['data-test-id']}-responses-checkbox`}>
                            <div className="flex items-center justify-center w-[16px] h-[16px] mr-[8px]">
                                <CheckDone size={16} color="rgba(0,0,0,0.88)" />
                            </div>
                            <span className="font-['Open_Sans'] text-[14px] leading-[22px] text-[rgba(0,0,0,0.25)]">
                                Responses
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Download Button */}
            <div className="flex gap-[8px] items-center justify-end">
                <Button
                    type="primary"
                    size="small"
                    onClick={handleExport}
                    data-test-id={`${props['data-test-id']}-download-btn`}
                >
                    Download
                </Button>
            </div>
        </div>
    );

    return (
        <ConfigProvider theme={theme}>
            <Popover
                content={content}
                trigger="click"
                open={open}
                onOpenChange={onOpenChange}
                placement="bottomRight"
                overlayClassName="export-menu-popover"
            >
                {children}
            </Popover>
        </ConfigProvider>
    );
};

export default ExportMenu;
